import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Select, SelectItem } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  useGetAgentQuery,
  usePostOfferMutation,
  usePostWebRtcSessionMutation,
} from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import {
  applyVoiceAgentParsedToClaimIntake,
  extractLookupIdentityFromParsed,
  type ClaimIntakeVoiceFormSetters,
} from './applyVoiceAgentParsedToClaimIntake';
import { buildClaimIntakeVoiceCustomerDetails } from './claimIntakeVoiceAgentConfig';
import {
  buildContractLookupResultEvent,
  normalizeClaimIntakeVoiceEventName,
  type ClaimIntakeVoiceAgentEvent,
  type ClaimIntakeVoiceOutboundEvent,
} from './claimIntakeVoiceEvents';
import {
  isVoiceAgentWithFormCapture,
  resolveVoiceAgentAssistantId,
  voiceAgentDisplayLabel,
} from './claimIntakeVoiceAgentUtils';
import type { ContractLookupTabHandle, ContractLookupVoiceResult } from './ContractLookupTab';
import {
  cleanupVoiceAgentWebRtc,
  sendVoiceAgentDataChannelMessage,
  sendVoiceAgentFormUpdate,
  startVoiceAgentWebRtcSession,
  type VoiceAgentControlEventMessage,
  type VoiceAgentFormCaptureMessage,
} from '../voiceagent/voiceAgentWebRtc';
import { STEPS, type ContractData } from './claimIntakeUtils';
import { buildVoiceFormUpdateFieldsFromContract } from './claimIntakeVoiceFormUpdate';

const LOG_PREFIX = '[ClaimIntakeVoiceFill]';

export type ClaimIntakeVoiceFillProps = Readonly<{
  token: string | null;
  formSetters: ClaimIntakeVoiceFormSetters;
  currentStep: number;
  onGoToStep: (step: number) => void;
  contractLookupRef: RefObject<ContractLookupTabHandle | null>;
  onRequestSubmitClaim: () => void;
}>;

function readContractNumberFromPayload(payload: Record<string, unknown>): string {
  if (typeof payload.contract_number === 'string') return payload.contract_number;
  if (typeof payload.contractNumber === 'string') return payload.contractNumber;
  return '';
}

function readIndexFromPayload(payload: Record<string, unknown>): number {
  const indexRaw = payload.index;
  if (typeof indexRaw === 'number') return indexRaw;
  if (typeof indexRaw === 'string') return Number.parseInt(indexRaw, 10);
  return Number.NaN;
}

function readVinFromPayload(payload: Record<string, unknown>): string {
  if (typeof payload.vin === 'string') return payload.vin;
  if (typeof payload.product_id === 'string') return payload.product_id;
  return '';
}

function voiceResultToOutbound(result: ContractLookupVoiceResult): ClaimIntakeVoiceOutboundEvent {
  switch (result.status) {
    case 'found':
      return buildContractLookupResultEvent({
        success: true,
        status: 'found',
        contracts: result.contracts,
        selected: result.contract,
      });
    case 'multiple':
      return buildContractLookupResultEvent({
        success: true,
        status: 'multiple',
        contracts: result.contracts,
      });
    case 'none':
      return buildContractLookupResultEvent({
        success: false,
        status: 'none',
        error: 'No contracts found.',
      });
    case 'invalid':
      return buildContractLookupResultEvent({
        success: false,
        status: 'invalid',
        error: result.message,
      });
    case 'error':
      return buildContractLookupResultEvent({
        success: false,
        status: 'error',
        error: result.message,
      });
  }
}

function lookupKey(vin: string, contractNumber: string): string {
  return `${vin.trim().toUpperCase()}|${contractNumber.trim().toLowerCase()}`;
}

function resolveSelectedContract(
  candidates: ContractData[],
  contractNumber: string,
  index: number
): ContractData | null {
  if (contractNumber.trim()) {
    const needle = contractNumber.trim().toLowerCase();
    return (
      candidates.find((c) => (c.contractNumber ?? '').trim().toLowerCase() === needle) ?? null
    );
  }
  if (Number.isFinite(index)) return candidates[index] ?? null;
  return null;
}

function trySelectContract(
  api: ContractLookupTabHandle,
  contractNumber: string,
  index: number
): boolean {
  if (contractNumber.trim()) return api.selectContractByNumber(contractNumber);
  if (Number.isFinite(index)) return api.selectContractByIndex(index);
  return false;
}

export function ClaimIntakeVoiceFill({
  token,
  formSetters,
  currentStep,
  onGoToStep,
  contractLookupRef,
  onRequestSubmitClaim,
}: ClaimIntakeVoiceFillProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'connecting' | 'connected'>('idle');
  const [selectedAssistantId, setSelectedAssistantId] = useState('');

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const pendingDataChannelMessagesRef = useRef<string[]>([]);
  const webrtcRefs = {
    peerConnectionRef,
    audioRef,
    localStreamRef,
    dataChannelRef,
    pendingDataChannelMessagesRef,
  };
  const currentStepRef = useRef(currentStep);
  const latestLookupRef = useRef({ vin: '', contractNumber: '' });
  const lastLookupKeyRef = useRef('');
  const lookupInFlightRef = useRef(false);
  const lastFormCaptureJsonRef = useRef('');
  const contractFetchedRef = useRef(false);

  // Keep latest callbacks for the long-lived WebRTC data-channel handlers.
  const formSettersRef = useRef(formSetters);
  const onGoToStepRef = useRef(onGoToStep);
  const onRequestSubmitClaimRef = useRef(onRequestSubmitClaim);
  const contractLookupRefInner = useRef(contractLookupRef);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    formSettersRef.current = formSetters;
  }, [formSetters]);

  useEffect(() => {
    onGoToStepRef.current = onGoToStep;
  }, [onGoToStep]);

  useEffect(() => {
    onRequestSubmitClaimRef.current = onRequestSubmitClaim;
  }, [onRequestSubmitClaim]);

  useEffect(() => {
    contractLookupRefInner.current = contractLookupRef;
  }, [contractLookupRef]);

  const { data: voiceAgents = [], isLoading: isLoadingAgents } = useGetAgentQuery(undefined, {
    skip: !token?.trim(),
  });

  const formCaptureAgents = useMemo(
    () => voiceAgents.filter(isVoiceAgentWithFormCapture),
    [voiceAgents]
  );

  const formCaptureAgentIds = useMemo(
    () => formCaptureAgents.map((a) => resolveVoiceAgentAssistantId(a)).join('|'),
    [formCaptureAgents]
  );

  useEffect(() => {
    if (!formCaptureAgentIds) return;
    const ids = formCaptureAgentIds.split('|').filter(Boolean);
    setSelectedAssistantId((prev) => (prev && ids.includes(prev) ? prev : ids[0] ?? ''));
  }, [formCaptureAgentIds]);

  const [postWebRtcSession] = usePostWebRtcSessionMutation();
  const [postOffer] = usePostOfferMutation();

  const isActive = status === 'connecting' || status === 'connected';
  const canStart =
    Boolean(selectedAssistantId.trim()) && formCaptureAgents.length > 0 && !isLoadingAgents;

  const endCall = useCallback(() => {
    cleanupVoiceAgentWebRtc(webrtcRefs, () => setStatus('idle'));
    lastLookupKeyRef.current = '';
    lastFormCaptureJsonRef.current = '';
    lookupInFlightRef.current = false;
    contractFetchedRef.current = false;
  }, []);

  useEffect(
    () => () => {
      endCall();
    },
    [endCall]
  );

  const replyToAgent = useCallback((message: ClaimIntakeVoiceOutboundEvent) => {
    const sent = sendVoiceAgentDataChannelMessage(webrtcRefs, message, LOG_PREFIX);
    if (!sent) {
      console.warn(`${LOG_PREFIX} failed to reply to agent`, message);
    }
  }, []);

  const pushContractFormUpdate = useCallback((contract: ContractData) => {
    const fields = buildVoiceFormUpdateFieldsFromContract(contract);
    console.log(`${LOG_PREFIX} form_update fields from contract`, fields);
    sendVoiceAgentFormUpdate(webrtcRefs, fields, {
      runLlm: true,
      logContext: LOG_PREFIX,
    });
  }, []);

  const openRepairsFormTab = useCallback(
    (eventName: 'go_to_next_page' | 'go_to_repairs_page') => {
      const repairsStep = 2;
      onGoToStepRef.current(repairsStep);
      replyToAgent({
        type: 'event',
        name: eventName,
        success: true,
        payload: {
          step: repairsStep,
          step_label: STEPS[repairsStep]?.label ?? 'Repairs',
          message: 'Opened Repairs tab. Skip fields already provided via form_update; collect remaining claim fields.',
        },
      });
      CtyShowToast(checkType.success, t('Opening Repairs to continue the claim form.'));
    },
    [replyToAgent, t]
  );

  const runContractLookup = useCallback(
    async (args: {
      vin: string;
      contractNumber: string;
      force?: boolean;
      source: string;
    }) => {
      const vin = args.vin.trim();
      const contractNumber = args.contractNumber.trim();
      if (!vin && !contractNumber) {
        replyToAgent(
          buildContractLookupResultEvent({
            success: false,
            status: 'invalid',
            error: 'Provide a VIN, a contract number, or both before looking up the contract.',
          })
        );
        return;
      }

      const key = lookupKey(vin, contractNumber);
      if (!args.force && lastLookupKeyRef.current === key) {
        console.log(`${LOG_PREFIX} skip duplicate lookup`, { key, source: args.source });
        return;
      }
      if (lookupInFlightRef.current) {
        console.log(`${LOG_PREFIX} lookup already in flight`, { source: args.source });
        return;
      }

      const api = contractLookupRefInner.current.current;
      if (!api) {
        replyToAgent(
          buildContractLookupResultEvent({
            success: false,
            status: 'error',
            error: 'Contract lookup is not ready. Stay on the Contract Lookup step.',
          })
        );
        return;
      }

      lookupInFlightRef.current = true;
      onGoToStepRef.current(0);
      CtyShowToast(checkType.success, t('Looking up contract…'));
      console.log(`${LOG_PREFIX} running Lookup Contract`, {
        source: args.source,
        vin,
        contractNumber,
      });

      try {
        const result = await api.lookupForVoice({ vin, contractNumber });
        console.log(`${LOG_PREFIX} lookup result`, result);
        if (result.status === 'found' || result.status === 'multiple') {
          lastLookupKeyRef.current = key;
        }
        replyToAgent(voiceResultToOutbound(result));
        if (result.status === 'found') {
          contractFetchedRef.current = true;
          pushContractFormUpdate(result.contract);
          CtyShowToast(checkType.success, t('Contract data is fetched.'));
        } else if (result.status === 'multiple') {
          CtyShowToast(
            checkType.success,
            t('Multiple contracts found. Please choose one with the voice agent.')
          );
        }
      } finally {
        lookupInFlightRef.current = false;
      }
    },
    [pushContractFormUpdate, replyToAgent, t]
  );

  const handleFormCapture = useCallback(
    (message: VoiceAgentFormCaptureMessage) => {
      if (message.parsed_fields) {
        const json = JSON.stringify(message.parsed_fields);
        if (json === lastFormCaptureJsonRef.current) {
          console.log(`${LOG_PREFIX} skip duplicate form_capture`);
          return;
        }
        lastFormCaptureJsonRef.current = json;
        console.log(`${LOG_PREFIX} form_capture`, message);
        console.log(`${LOG_PREFIX} parsed_fields (JSON)`, JSON.stringify(message.parsed_fields, null, 2));
      } else {
        console.log(`${LOG_PREFIX} form_capture`, message);
      }

      if (!message.success || !message.parsed_fields) {
        if (!message.success) {
          console.warn(`${LOG_PREFIX} form_capture failed`, message.error ?? message);
        }
        return;
      }

      const applied = applyVoiceAgentParsedToClaimIntake(
        message.parsed_fields,
        formSettersRef.current
      );
      console.log(`${LOG_PREFIX} form fields applied`, applied);

      const identity = extractLookupIdentityFromParsed(message.parsed_fields);
      if (identity.vin) latestLookupRef.current.vin = identity.vin;
      if (identity.contractNumber) {
        latestLookupRef.current.contractNumber = identity.contractNumber;
      }

      // Fill inputs then run the same action as clicking "Lookup Contract".
      if (identity.vin || identity.contractNumber) {
        void runContractLookup({
          vin: latestLookupRef.current.vin,
          contractNumber: latestLookupRef.current.contractNumber,
          source: 'form_capture',
        });
      }
    },
    [runContractLookup]
  );

  const handleGoToNextPage = useCallback(() => {
    // After contract fetch, "continue to form" opens Repairs (3rd tab), not Vehicle & Claims.
    if (contractFetchedRef.current && currentStepRef.current <= 1) {
      openRepairsFormTab('go_to_next_page');
      return;
    }
    const next = Math.min(currentStepRef.current + 1, STEPS.length - 1);
    onGoToStepRef.current(next);
    replyToAgent({
      type: 'event',
      name: 'go_to_next_page',
      success: true,
      payload: { step: next, step_label: STEPS[next]?.label ?? '' },
    });
  }, [openRepairsFormTab, replyToAgent]);

  const handleGoToPreviousPage = useCallback(() => {
    const prev = Math.max(currentStepRef.current - 1, 0);
    onGoToStepRef.current(prev);
    replyToAgent({
      type: 'event',
      name: 'go_to_previous_page',
      success: true,
      payload: { step: prev, step_label: STEPS[prev]?.label ?? '' },
    });
  }, [replyToAgent]);

  const handleSubmitClaimEvent = useCallback(() => {
    onGoToStepRef.current(STEPS.length - 1);
    onRequestSubmitClaimRef.current();
    replyToAgent({
      type: 'event',
      name: 'submit_claim',
      success: true,
      payload: { message: 'Submit claim requested.' },
    });
  }, [replyToAgent]);

  const handleFetchContractEvent = useCallback(
    async (payload: Record<string, unknown>) => {
      const vin = readVinFromPayload(payload).trim() || latestLookupRef.current.vin;
      const contractNumber =
        readContractNumberFromPayload(payload).trim() || latestLookupRef.current.contractNumber;
      await runContractLookup({
        vin,
        contractNumber,
        force: true,
        source: 'fetch_contract',
      });
    },
    [runContractLookup]
  );

  const handleSelectContractEvent = useCallback(
    (payload: Record<string, unknown>) => {
      const api = contractLookupRefInner.current.current;
      if (!api) {
        replyToAgent({
          type: 'event',
          name: 'contract_lookup_result',
          success: false,
          error: 'Contract lookup is not ready.',
          payload: { status: 'error' },
        });
        return;
      }

      const contractNumber = readContractNumberFromPayload(payload);
      const index = readIndexFromPayload(payload);
      if (!trySelectContract(api, contractNumber, index)) {
        replyToAgent({
          type: 'event',
          name: 'contract_lookup_result',
          success: false,
          error: 'Could not select that contract. Ask the caller to confirm the contract number.',
          payload: { status: 'error' },
        });
        return;
      }

      const candidates = api.getLookupCandidates();
      const chosen = resolveSelectedContract(candidates, contractNumber, index);
      contractFetchedRef.current = true;
      replyToAgent(
        buildContractLookupResultEvent({
          success: true,
          status: 'found',
          contracts: chosen ? [chosen] : candidates,
          selected: chosen,
        })
      );
      if (chosen) pushContractFormUpdate(chosen);
      CtyShowToast(checkType.success, t('Contract data is fetched.'));
    },
    [pushContractFormUpdate, replyToAgent, t]
  );

  const handleVoiceEvent = useCallback(
    async (message: VoiceAgentControlEventMessage) => {
      const event = message as ClaimIntakeVoiceAgentEvent;
      const canonical = normalizeClaimIntakeVoiceEventName(event.name);
      console.log(`${LOG_PREFIX} event`, event, { canonical });

      if (!canonical) {
        // Ignore our own outbound replies if they echo on the channel.
        if (event.name === 'contract_lookup_result') return;
        console.warn(`${LOG_PREFIX} unknown event`, event.name);
        return;
      }

      switch (canonical) {
        case 'go_to_next_page':
          handleGoToNextPage();
          return;
        case 'go_to_repairs_page':
          openRepairsFormTab('go_to_repairs_page');
          return;
        case 'go_to_previous_page':
          handleGoToPreviousPage();
          return;
        case 'submit_claim':
          handleSubmitClaimEvent();
          return;
        case 'fetch_contract':
          await handleFetchContractEvent(event.payload ?? {});
          return;
        case 'select_contract':
          handleSelectContractEvent(event.payload ?? {});
          return;
        default:
          return;
      }
    },
    [
      handleFetchContractEvent,
      handleGoToNextPage,
      handleGoToPreviousPage,
      handleSelectContractEvent,
      handleSubmitClaimEvent,
      openRepairsFormTab,
    ]
  );

  // Stable wrappers for the WebRTC session (created once per call).
  const handleFormCaptureRef = useRef(handleFormCapture);
  const handleVoiceEventRef = useRef(handleVoiceEvent);
  useEffect(() => {
    handleFormCaptureRef.current = handleFormCapture;
  }, [handleFormCapture]);
  useEffect(() => {
    handleVoiceEventRef.current = handleVoiceEvent;
  }, [handleVoiceEvent]);

  const startCall = useCallback(async () => {
    if (!token) {
      CtyShowToast(checkType.error, t('Authentication required. Please log in.'));
      return;
    }
    const assistantId = selectedAssistantId.trim();
    if (!assistantId) {
      CtyShowToast(checkType.error, t('Please select a voice agent.'));
      return;
    }
    setStatus('connecting');
    lastLookupKeyRef.current = '';
    lastFormCaptureJsonRef.current = '';
    contractFetchedRef.current = false;
    pendingDataChannelMessagesRef.current = [];
    const customerDetails = buildClaimIntakeVoiceCustomerDetails();
    console.log(`${LOG_PREFIX} web_RTC session body`, {
      assistant_id: assistantId,
      phonenumber: '',
      customer_details: customerDetails,
    });
    await startVoiceAgentWebRtcSession({
      assistantId,
      token,
      t,
      postWebRtcSession,
      postOffer,
      refs: {
        peerConnectionRef,
        audioRef,
        localStreamRef,
        dataChannelRef,
        pendingDataChannelMessagesRef,
      },
      logContext: LOG_PREFIX,
      customerDetails,
      onFormCapture: (message) => handleFormCaptureRef.current(message),
      onVoiceEvent: (message) => {
        void handleVoiceEventRef.current(message);
      },
      onClientMessageAck: () => {
        console.log(`${LOG_PREFIX} client_message_ack received`);
      },
      onConnected: () => setStatus('connected'),
      onDisconnected: () => setStatus('idle'),
      onRemoteTrackUnmute: () => {
        CtyShowToast(checkType.success, t('Voice agent is ready. You can start speaking.'));
      },
    });
  }, [postOffer, postWebRtcSession, selectedAssistantId, t, token]);

  const onPress = () => {
    if (isActive) {
      endCall();
      return;
    }
    void startCall();
  };

  const statusLabel = useMemo(() => {
    if (status === 'connected') return t('Voice active');
    if (status === 'connecting') return t('Connecting');
    return null;
  }, [status, t]);

  const selectPlaceholder = useMemo(() => {
    if (isLoadingAgents) return t('Loading voice agents…');
    if (formCaptureAgents.length === 0) return t('No form-fill voice agents');
    return t('Select voice agent');
  }, [formCaptureAgents.length, isLoadingAgents, t]);

  return (
    <div className="flex flex-wrap items-center gap-2 shrink-0 max-w-full">
      <Select
        aria-label={t('Voice agent for form filling')}
        placeholder={selectPlaceholder}
        selectedKeys={selectedAssistantId ? [selectedAssistantId] : []}
        onSelectionChange={(keys) => {
          const key = Array.from(keys)[0];
          setSelectedAssistantId(typeof key === 'string' ? key : String(key ?? ''));
        }}
        isDisabled={isActive || isLoadingAgents || formCaptureAgents.length === 0}
        isLoading={isLoadingAgents}
        size="sm"
        className="min-w-[200px] max-w-[min(100%,320px)]"
        classNames={{ trigger: 'min-h-9' }}
      >
        {formCaptureAgents.map((agent) => {
          const id = resolveVoiceAgentAssistantId(agent);
          return (
            <SelectItem key={id} value={id} textValue={voiceAgentDisplayLabel(agent)}>
              {voiceAgentDisplayLabel(agent)}
            </SelectItem>
          );
        })}
      </Select>
      {statusLabel ? (
        <Chip size="sm" color={status === 'connected' ? 'success' : 'warning'} variant="flat">
          {statusLabel}
        </Chip>
      ) : null}
      <CtyButton
        size="sm"
        color={isActive ? 'danger' : 'primary'}
        variant={isActive ? 'solid' : 'bordered'}
        onPress={onPress}
        isLoading={status === 'connecting'}
        isDisabled={!isActive && !canStart}
        startContent={!isActive ? <Icon icon="mdi:microphone" width={18} /> : undefined}
      >
        {isActive ? t('End voice fill') : t('Start voice fill')}
      </CtyButton>
      <audio ref={audioRef} autoPlay className="sr-only" tabIndex={-1} />
    </div>
  );
}
